import { useTranslation } from "react-i18next";
import { SectionTitle } from "@/components/ui/SectionTitle";
import { BlockHeader, type ITeamDisplayData } from "./add-project-team-form";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Component } from "lucide-react";
import { Link } from "react-router-dom";
import { PATHS } from "@/routes/paths";

interface Props {
  teams: ITeamDisplayData[];
}

export const RightBlock = ({ teams }: Props) => {
  const { t } = useTranslation(["projects", "common"]);
  return (
    <div className="h-144 lg:h-152 min-w-64 shrink-0 flex-1 border border-primary rounded-sm overflow-hidden flex flex-col">
      <BlockHeader className="flex justify-between items-center">
        <SectionTitle title={t("projects:assigned_teams")} noMarginBottom />
        <MemberCount count={teams.length} />
      </BlockHeader>

      <div className="flex flex-col gap-3 sm:gap-4 overflow-y-auto p-2 sm:p-4">
        {!teams.length ? (
          <EmptyState />
        ) : (
          teams.map((team, i) => (
            <div key={i} className="bg-primary/10 p-2 sm:p-3 rounded-sm">
              <SectionTitle
                title={team.name ?? t("projects:unnamed_team")}
                noMarginBottom
                className="mb-2 text-sm sm:text-base"
              />
              <div className="grid grid-cols-1 sm:grid-cols-2 2xl:grid-cols-3 gap-2 sm:gap-3 max-h-72 sm:max-h-96 overflow-y-auto">
                {!team?.members?.length ? (
                  <p className="text-sm">
                    {t("projects:no_team_members")}&nbsp;
                    <Link
                      to={PATHS.COMPANY.COMPANY_TEAMS_PATHS.ROOT}
                      className="text-primary hover:underline"
                    >
                      {t("projects:manage_team")}
                    </Link>
                  </p>
                ) : (
                  team?.members?.map((member, i) => (
                    <div
                      key={i}
                      className="border rounded-sm p-2 flex gap-2 items-center"
                    >
                      <Avatar className="shrink-0 h-8 w-8 sm:h-10 sm:w-10">
                        <AvatarFallback className="text-xs sm:text-sm">
                          {team.name?.slice(0, 2).toUpperCase() ?? "T"}
                        </AvatarFallback>
                        <AvatarImage src={member.profileImage ?? undefined} />
                      </Avatar>

                      <div className="flex-1 min-w-0">
                        <p className="text-xs sm:text-sm uppercase truncate">
                          {member.name}
                        </p>
                        <p className="text-xs text-muted-foreground truncate">
                          {member.roleName}
                        </p>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

const MemberCount = ({ count }: { count: number }) =>
  !count ? null : (
    <div className="flex-none flex items-center justify-center min-w-7 h-7 px-1 bg-white border border-primary rounded-full text-primary text-xs font-bold tabular-nums shadow-xs">
      {count}
    </div>
  );

const EmptyState = () => {
  const { t } = useTranslation(["projects", "common"]);
  return (
    <div className="border rounded-sm p-4 flex flex-col items-center">
      <Avatar>
        <AvatarFallback className="bg-primary/10 rounded-full">
          <Component size={24} className="text-primary" />
        </AvatarFallback>
      </Avatar>
      <h6 className="text-base font-semibold mt-2">
        {t("projects:no_teams_selected_yet")}
      </h6>
      <p className="text-black/50 -mt-1">
        {t("projects:select_teams_from_the_left_to_get_started")}
      </p>
    </div>
  );
};
